import { useEffect, useState } from "react";
import { DEPARTMENTS } from "@/lib/departments";
import { Link } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { MODES, SLOTS, REVENUES, EMPLOYEES, TIMINGS, Chips, Field, SelectField, Group, ChipField } from "@/components/DiscoveryFormParts";

const EMPTY = {
  name: "", email: "", phone: "", company_sector: "", region: "", revenue_range: "", employees_range: "", timing: "",
  meeting_mode: "telephone", preferred_date: "", preferred_slot: "indifferent", message: "", website: "",
};
const today = new Date().toISOString().slice(0, 10);

export default function DiscoveryForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sectors, setSectors] = useState([]);
  useEffect(() => { api.get("/meta/reference").then((r) => setSectors(r.data.sectors)).catch(() => {}); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try { await api.post("/discovery-requests", form); setSent(true); setForm(EMPTY); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSending(false);
  };

  if (sent) return (
    <div data-testid="rdv-success" className="py-16 text-center">
      <CheckCircle2 className="w-14 h-14 text-[#E0C173] mx-auto" strokeWidth={1.5} />
      <h2 className="font-display text-2xl font-semibold text-[#1B2A4A] mt-4">Demande de RDV envoyée</h2>
      <p className="text-[#475569] mt-2 max-w-md mx-auto">Merci pour votre confiance. Un conseiller PROAC Transmission vous contacte très rapidement pour fixer votre rendez-vous découverte, en toute discrétion.</p>
      <Link to="/" data-testid="rdv-back-home" className="inline-block mt-6 text-sm text-[#1B2A4A] border-b border-[#E0C173]">Retour à l'accueil</Link>
    </div>
  );

  return (
    <form data-testid="rdv-form" onSubmit={submit} className="space-y-10">
      <Group step="1" title="Vous">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field id="name" label="Nom et prénom *" value={form.name} onChange={set("name")} required minLength={2} />
          <Field id="phone" label="Téléphone *" type="tel" value={form.phone} onChange={set("phone")} required minLength={6} />
        </div>
        <Field id="email" label="Email *" type="email" value={form.email} onChange={set("email")} required />
        <input type="text" value={form.website} onChange={(e) => set("website")(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] w-px h-px opacity-0" />
      </Group>

      <Group step="2" title="Votre entreprise">
        <div className="grid sm:grid-cols-2 gap-5">
          <SelectField id="company_sector" label="Secteur d'activité" value={form.company_sector} onChange={set("company_sector")} options={sectors} placeholder="Choisir un secteur…" />
          <SelectField id="region" label="Département" value={form.region} onChange={set("region")} options={DEPARTMENTS} placeholder="Choisir un département…" />
        </div>
        <ChipField label="Chiffre d'affaires"><Chips name="revenue_range" options={REVENUES} value={form.revenue_range} onChange={set("revenue_range")} /></ChipField>
        <ChipField label="Effectif"><Chips name="employees_range" options={EMPLOYEES} value={form.employees_range} onChange={set("employees_range")} /></ChipField>
        <ChipField label="Horizon de cession"><Chips name="timing" options={TIMINGS} value={form.timing} onChange={set("timing")} /></ChipField>
      </Group>

      <Group step="3" title="Votre rendez-vous découverte">
        <ChipField label="Format souhaité"><Chips name="meeting_mode" options={MODES} value={form.meeting_mode} onChange={set("meeting_mode")} /></ChipField>
        <div className="grid sm:grid-cols-2 gap-5 items-end">
          <Field id="preferred_date" label="Date souhaitée (indicative)" type="date" min={today} value={form.preferred_date} onChange={set("preferred_date")} />
          <ChipField label="Moment de la journée"><Chips name="preferred_slot" options={SLOTS} value={form.preferred_slot} onChange={set("preferred_slot")} /></ChipField>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-[#475569] mb-1.5">Quelques mots sur votre projet (facultatif)</div>
          <Textarea data-testid="rdv-message" value={form.message} onChange={(e) => set("message")(e.target.value)} rows={4} placeholder="Départ en retraite, recherche d'un repreneur pour vos équipes, nouveau projet…" className="rounded-none border-gray-300" />
        </div>
      </Group>

      <div className="border-t border-gray-100 pt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <p className="text-xs text-gray-500 max-w-sm">Échange gratuit et sans engagement. Vos informations restent strictement confidentielles et ne sont jamais transmises sans votre accord.</p>
        <button data-testid="rdv-submit" type="submit" disabled={sending} className="inline-flex items-center justify-center gap-2 bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-8 py-3.5 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50">
          <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Demander mon RDV découverte"}
        </button>
      </div>
    </form>
  );
}
